import { Dimensions } from "react-native";
import { swipeDirections } from "../components/GestureView";
import AudioManager from "../src/AudioManager";
import ModelLoader from "../src/ModelLoader";
import Characters from "./Characters";
import { TweenMax } from "gsap";
import { utils } from "expo-three";
import {
  CrossyCamera,
  CrossyGameMap,
  CrossyRenderer,
  CrossyScene,
} from "./CrossyGame";
import CrossyPlayer from "./CrossyPlayer";
import {
  CAMERA_EASING,
  DEBUG_CAMERA_CONTROLS,
  groundLevel,
  PI_2,
  sceneColor,
  startingRow,
} from "./GameSettings";

import { SpotLight } from "three";

const initialState = {
  id: Characters.bacon.id,
  name: Characters.bacon.name,
  index: Characters.bacon.index,
};

const normalizeAngle = (angle) => {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
};

export default class Engine {
  updateScale = () => {
    const { width, height, scale } = Dimensions.get("window");
    if (this.camera) {
      this.camera.updateScale({ width, height, scale });
    }
    if (this.renderer) {
      this.renderer.setSize(width * scale, height * scale);
    }
  };

  setupGame = (character) => {
    this.scene = new CrossyScene({});

    this.camera = new CrossyCamera();

    if (DEBUG_CAMERA_CONTROLS) {
      // this.debugControls = new THREE.OrbitControls(this.camera);
    }

    this.scene.worldWithCamera.position.z = -startingRow;

    this.updateScale();

    this.gameMap = new CrossyGameMap({
      heroWidth: 0.7,
      scene: this.scene,
      onCollide: this.onCollide,
    });

    this.camCount = 0;

    // Mesh
    this._hero = new CrossyPlayer(character);
    this._hero.scene_world = this.scene.world;
    this._hero.scene = this.scene;

    this.scene.world.add(this._hero);

    this.scene.createParticles();
  };

  isGameEnded() {
    return !this._hero.isAlive || this._isGameStateEnded();
  }

  onCollide = async (obstacle = {}, type = "", collision) => {
    if (this.isGameEnded()) {
      return;
    }
    this._hero.isAlive = false;
    this._hero.stopIdle();
    if (type != "water") {
      type = Characters[this._hero._character].deathParticle
        ? Characters[this._hero._character].deathParticle
        : "feathers";
    }
    if (collision === "car") {
      AudioManager.playCarHitSound();
      AudioManager.playDeathSound();
    } else if (collision === "train") {
      await AudioManager.playAsync(AudioManager.sounds.train.die[`0`]);
      AudioManager.playDeathSound();
    }
    this.scene.useParticle(this._hero, type, obstacle.speed);

    if (this._hero.isProtected) {
      this._hero.isProtected = false;
      this._hero.disableMoving = true;
      this._hero.dropItem();
      TweenMax.to(this._hero.scale, 1, {
        x: 0.01,
        y: 0.01,
        z: 0.01,
        ease: "elastic.out(1, 0.3)",
      });

      const spotLight = new SpotLight(0x0080ff, 10);
      spotLight.position.set(
        this._hero.checkpoint.x,
        this._hero.checkpoint.y + 10,
        this._hero.checkpoint.z - 12
      );
      spotLight.angle = 0;
      spotLight.penumbra = 1;
      spotLight.power = 10;
      spotLight.decay = 2;

      spotLight.intensity = 10;

      spotLight.target.position.set(
        this._hero.checkpoint.x,
        this._hero.checkpoint.y,
        this._hero.checkpoint.z - 12
      );
      spotLight.target = this._hero;
      spotLight.target.updateMatrixWorld();
      setTimeout(() => {
        this._hero.reset();
        this._hero.scale.set(0.01, 0.01, 0.01);
        this._hero.position.set(
          this._hero.checkpoint.x,
          this._hero.checkpoint.y,
          this._hero.checkpoint.z
        );

        this.scene.add(spotLight);

        const spotlightAngleOpen = setInterval(() => {
          spotLight.angle += Math.PI / 512;
          if (spotLight.angle >= Math.PI / 32) {
            clearInterval(spotlightAngleOpen);
            TweenMax.to(this._hero.scale, 1, {
              x: 1,
              y: 1,
              z: 1,
              ease: "elastic.out(1, 0.3)",
            });
            setTimeout(() => {
              const spotlightAngleClose = setInterval(() => {
                spotLight.angle -= Math.PI / 512;
                if (spotLight.angle <= 0.0001) {
                  clearInterval(spotlightAngleClose);
                  this.scene.remove(spotLight);
                  this._hero.disableMoving = false;
                }
              }, 100);
            }, 1000);
          }
        }, 100);
        this._hero.targetPosition = this._hero.position;
        this._hero.rotation.set(0, 0, 0);
      }, 3000);
    } else {
      this.scene.rumble();
      this.gameOver();
    }
  };

  // Setup initial scene
  init = () => {
    this.onGameInit();

    this.camera.position.z = 1;
    this._hero.reset();

    this.scene.resetParticles(this._hero.position);

    this.camCount = 0;

    this.gameMap.reset();

    this._hero.idle();
    this.gameMap.init();

    this.onGameReady();
  };

  // Move scene forward
  forwardScene = () => {
    this.scene.world.position.z -=
      (this._hero.position.z - startingRow + this.scene.world.position.z) *
      CAMERA_EASING;
    this.scene.world.position.x = -Math.min(
      2,
      Math.max(
        -2,
        this.scene.world.position.x +
          (this._hero.position.x - this.scene.world.position.x) * CAMERA_EASING
      )
    );

    // normal camera speed
    if (-this.scene.world.position.z - this.camCount > 1.0) {
      this.camCount = -this.scene.world.position.z;
      this.gameMap.newRow();
    }
  };

  // Reset variables, restart game
  gameOver = () => {
    this._hero.moving = false;
    // Stop player from finishing a movement
    this._hero.stopAnimations();
    this.onGameEnded();
    this.scene.timeManager.stopTimeOnGameOver();
    this._hero.dropItem();
    // this.gameState = State.Game.gameOver;

    // this.props.setGameState(this.gameState);
  };

  tick = (dt) => {
    // this.drive();
    this.gameMap.tick(dt, this._hero);

    if (!this._hero.moving) {
      this._hero.moveOnEntity();
      this._hero.moveOnCar();
      this.checkIfUserHasFallenOutOfFrame();
    }
    this.forwardScene();
  };

  checkIfUserHasFallenOutOfFrame = () => {
    if (this.isGameEnded()) {
      return;
    }
    if (this._hero.position.z < this.camera.position.z - 1) {
      this.scene.rumble();
      this.gameOver();
      AudioManager.playDeathSound();
    }

    // Check if offscreen
    if (this._hero.position.x < -5 || this._hero.position.x > 5) {
      this.scene.rumble();
      this.gameOver();
      AudioManager.playDeathSound();
    }
  };

  pause() {
    cancelAnimationFrame(this.raf);
  }

  unpause() {
    const render = () => {
      this.raf = requestAnimationFrame(render);
      const time = Date.now();
      this.tick(time);
      this.renderer.render(this.scene, this.camera);

      // NOTE: At the end of each frame, notify `Expo.GLView` with the below
      this.renderer.__gl.endFrameEXP();
    };
    render();
  }

  updateScore = () => {
    const position = Math.max(Math.floor(this._hero.position.z) - 8, 0);
    this.onUpdateScore(position);
  };

  moveWithDirection = (direction) => {
    if (this.isGameEnded() || this._hero.disableMoving) {
      return;
    }
    const { SWIPE_UP, SWIPE_DOWN, SWIPE_LEFT, SWIPE_RIGHT } = swipeDirections;

    this._hero.ridingOn = null;

    if (!this._hero.initialPosition) {
      this._hero.initialPosition = this._hero.position;
      this._hero.targetPosition = this._hero.initialPosition;
    }

    this._hero.skipPendingMovement();

    let velocity = { x: 0, z: 0 };

    this._hero.targetRotation = normalizeAngle(this._hero.rotation.y);
    // const normalizedRotation = normalizeAngle(this._hero.rotation.y)
    switch (direction) {
      case SWIPE_LEFT:
        {
          this._hero.targetRotation = PI_2; // calculateRotation(targetRotation, Math.PI / 2);

          velocity = { x: 1, z: 0 };

          this._hero.targetPosition = {
            x: this._hero.initialPosition.x + 1,
            y: this._hero.initialPosition.y,
            z: this._hero.initialPosition.z,
          };
          this._hero.moving = true;
        }
        break;
      case SWIPE_RIGHT:
        {
          if (this._hero.targetPosition === 0) {
            this._hero.targetPosition = -PI_2;
          } else if (
            (this._hero.targetRotation | 0) !== -(PI_2 | 0) &&
            (this._hero.targetRotation | 0) !== ((Math.PI + PI_2) | 0)
          ) {
            this._hero.targetRotation = Math.PI + PI_2;
          }
          velocity = { x: -1, z: 0 };

          this._hero.targetPosition = {
            x: this._hero.initialPosition.x - 1,
            y: this._hero.initialPosition.y,
            z: this._hero.initialPosition.z,
          };
          this._hero.moving = true;
        }
        break;
      case SWIPE_UP:
        {
          this._hero.targetRotation = 0;
          let rowObject =
            this.gameMap.getRow(this._hero.initialPosition.z) || {};
          if (rowObject.type === "road") {
            AudioManager.playPassiveCarSound();
          }

          let shouldRound = true; // rowObject.type !== 'water';
          velocity = { x: 0, z: 1 };

          this._hero.targetPosition = {
            x: this._hero.initialPosition.x,
            y: this._hero.initialPosition.y,
            z: this._hero.initialPosition.z + 1,
          };

          if (shouldRound) {
            this._hero.targetPosition.x = Math.round(
              this._hero.targetPosition.x
            );
            const { ridingOn } = this._hero;
            if (ridingOn && ridingOn.dir) {
              if (ridingOn.dir < 0) {
                this._hero.targetPosition.x = Math.floor(
                  this._hero.targetPosition.x
                );
              } else if (ridingOn.dir > 0) {
                this._hero.targetPosition.x = Math.ceil(
                  this._hero.targetPosition.x
                );
              } else {
                this._hero.targetPosition.x = Math.round(
                  this._hero.targetPosition.x
                );
              }
            }
          }
          if (
            this.gameMap.getRow(this._hero.initialPosition.z + velocity.z)
              .type === "grass" &&
            this._hero.isProtected
          ) {
            this._hero.checkpoint = {
              x: this._hero.targetPosition.x,
              y: this._hero.targetPosition.y,
              z: this._hero.targetPosition.z,
            };
          }

          this._hero.moving = true;
        }
        break;
      case SWIPE_DOWN:
        {
          this._hero.targetRotation = Math.PI;
          const row = (this.gameMap.getRow(this._hero.initialPosition.z) || {})
            .type;
          let shouldRound = true; //row !== 'water';
          velocity = { x: 0, z: -1 };

          this._hero.targetPosition = {
            x: this._hero.initialPosition.x,
            y: this._hero.initialPosition.y,
            z: this._hero.initialPosition.z - 1,
          };
          if (shouldRound) {
            this._hero.targetPosition.x = Math.round(
              this._hero.targetPosition.x
            );
            const { ridingOn } = this._hero;
            if (ridingOn && ridingOn.dir) {
              if (ridingOn.dir < 0) {
                this._hero.targetPosition.x = Math.floor(
                  this._hero.targetPosition.x
                );
              } else if (ridingOn.dir > 0) {
                this._hero.targetPosition.x = Math.ceil(
                  this._hero.targetPosition.x
                );
              } else {
                this._hero.targetPosition.x = Math.round(
                  this._hero.targetPosition.x
                );
              }
            }
          }
          this._hero.moving = true;
        }
        break;
    }

    const updateCarriedItemPosition = () => {
      if (!this._hero.carriedItem) {
        return;
      }
      utils.alignMesh(this._hero.carriedItem.mesh, { x: 0.5, z: 0.5, y: 1.0 });
      this._hero.carriedItem.mesh.position.set(
        this._hero.targetPosition.x,
        this._hero.height + 0.5,
        this._hero.targetPosition.z
      );
    };

    // updateCarriedItemPosition();

    // Check collision using the computed movement.
    if (this.gameMap.treeCollision(this._hero.targetPosition)) {
      // If we collide with an object, then reset the target position so the character just jumps up.
      this._hero.targetPosition = {
        x: this._hero.initialPosition.x,
        y: this._hero.initialPosition.y,
        z: this._hero.initialPosition.z,
      };
      this._hero.moving = false;
    }

    if (this.gameMap.collectItemDetection(this._hero)) {
      this.scene.world.add(this._hero.carriedItem.mesh);
      updateCarriedItemPosition();
    }

    const targetRow =
      this.gameMap.getRow(this._hero.initialPosition.z + velocity.z) || {};
    let finalY = targetRow.entity.top || groundLevel;
    // If the next move is into the river, then we want to jump into it.
    if (targetRow.type === "water") {
      const ridable = targetRow.entity.getRidableForPosition(
        this._hero.targetPosition
      );
      if (!ridable) {
        finalY = targetRow.entity.getPlayerSunkenPosition();
      } else {
        finalY =
          targetRow.entity.getPlayerLowerBouncePositionForEntity(ridable);
      }
    }

    AudioManager.playMoveSound();

    this._hero.targetPosition.y = finalY;

    this._hero.commitMovementAnimations({
      onComplete: () => this.updateScore(),
    });
  };

  beginMoveWithDirection = () => {
    if (this.isGameEnded()) {
      return;
    }
    this._hero.runPosieAnimation();
  };

  _onGLContextCreate = async (gl) => {
    const { drawingBufferWidth: width, drawingBufferHeight: height } = gl;

    this.renderer = new CrossyRenderer({
      gl,
      antialias: true,
      width,
      height,
      clearColor: sceneColor,
    });

    this.unpause();
  };
}
